/**
 * @fileoverview Status mappings page for BaseLinker order statuses.
 * @module features/status-mappings/pages/StatusMappingsPage
 */

import { ReactElement, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { ArrowLeft } from 'lucide-react'

import PageMeta from '@/shared/components/common/PageMeta'
import {
  Button,
  ScrollReveal,
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '@/shared/components/ui'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Kbd } from '@/components/ui/kbd'
import {
  ViewTypeEnum,
  type ViewType,
  listStatusMappings,
  createStatusMapping,
  updateStatusMapping,
  deleteStatusMapping,
} from '@/features/status-mappings/api/statusMappingsService'

/** Unified row type for status mappings (all statuses are now pre-populated) */
interface StatusRow {
  id: number
  externalStatus: string
  statusName: string | null
  viewType: ViewType | null
}

/**
 * Status Mappings page for managing BaseLinker order status mappings.
 */
export function StatusMappingsPage(): ReactElement {
  const { t } = useTranslation('features-settings')
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; name: string } | null>(null)

  // Handle ESC key to go back
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !deleteTarget) {
        navigate('/dashboard/settings')
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [navigate, deleteTarget])

  const viewTypeLabel = (v: ViewType | null): string => {
    if (v === null) return t('cards.statusMappings.options.notMapped')
    if (v === 'SALES_ANALYTICS') return t('cards.statusMappings.options.sales')
    if (v === 'CANCELLATION_ANALYTICS') return t('cards.statusMappings.options.cancellation')
    return t('cards.statusMappings.options.ignore')
  }

  const mappingsQuery = useQuery({
    queryKey: ['status-mappings'],
    queryFn: () => listStatusMappings(),
    staleTime: 60_000,
  })

  // All statuses are now pre-populated with viewType = null for unconfigured ones
  const allRows = useMemo((): StatusRow[] => {
    return (mappingsQuery.data ?? [])
      .map(m => ({
        id: m.id,
        externalStatus: m.externalStatus,
        statusName: m.statusName ?? null,
        viewType: m.viewType,
      }))
      .sort((a, b) => {
        const la = (a.statusName ?? a.externalStatus).toLowerCase()
        const lb = (b.statusName ?? b.externalStatus).toLowerCase()
        return la.localeCompare(lb)
      })
  }, [mappingsQuery.data])

  const createMutation = useMutation({
    mutationFn: (params: { externalStatus: string; viewType: ViewType }) =>
      createStatusMapping(params),
    onSuccess: async () => {
      toast.success(t('cards.statusMappings.created'))
      await qc.invalidateQueries({ queryKey: ['status-mappings'] })
    },
    onError: (e: unknown) => toast.error((e as Error)?.message ?? 'Create failed'),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, next }: { id: number; next: ViewType }) =>
      updateStatusMapping(id, { viewType: next }),
    onSuccess: async () => {
      toast.success(t('cards.statusMappings.updated'))
      await qc.invalidateQueries({ queryKey: ['status-mappings'] })
    },
    onError: (e: unknown) => toast.error((e as Error)?.message ?? 'Update failed'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteStatusMapping(id),
    onSuccess: async () => {
      toast.success(t('cards.statusMappings.deleted'))
      setDeleteTarget(null)
      await qc.invalidateQueries({ queryKey: ['status-mappings'] })
    },
    onError: (e: unknown) => toast.error((e as Error)?.message ?? 'Delete failed'),
  })

  const handleViewTypeChange = (row: StatusRow, newValue: string): void => {
    if (newValue === '') {
      // Changed to "Not mapped" — delete the mapping (keeping for backwards compatibility)
      setDeleteTarget({ id: row.id, name: row.statusName ?? row.externalStatus })
    } else {
      const viewType = newValue as ViewType
      // All rows now have IDs since statuses are pre-populated
      updateMutation.mutate({ id: row.id, next: viewType })
    }
  }

  const isLoading = mappingsQuery.isLoading

  return (
    <div className="min-h-screen pb-16">
      <PageMeta
        title={t('cards.statusMappings.title')}
        description={t('cards.statusMappings.pageDescription')}
      />
      <div className="mx-auto max-w-2xl space-y-8 px-4 lg:px-8 pb-6 pt-10">
        <ScrollReveal blur>
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-3">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/dashboard/settings')}
                    className="gap-1.5 -ml-2"
                  >
                    <ArrowLeft className="size-4" />
                    {t('common.back')}
                    <Kbd>Esc</Kbd>
                  </Button>
                </div>
                <h1 className="text-3xl font-semibold text-foreground mt-2">
                  {t('cards.statusMappings.pageTitle')}
                </h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  {t('cards.statusMappings.pageDescription')}
                </p>
              </div>
            </div>
          </div>
        </ScrollReveal>

        {/* Status mappings table */}
        <ScrollReveal delay={0.1} blur>
          <div className="rounded-lg border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="pl-4">
                  {t('cards.statusMappings.table.status')}
                </TableHead>
                <TableHead className="text-right w-[220px] pr-4">
                  {t('cards.statusMappings.table.treatAs')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {allRows.map(row => (
                <TableRow
                  key={row.externalStatus}
                  className={row.viewType === null ? 'bg-muted/30' : undefined}
                >
                  <TableCell className="pl-4">
                    <span className="font-medium text-foreground">
                      {row.statusName ?? row.externalStatus}
                    </span>
                  </TableCell>
                  <TableCell className="text-right pr-4">
                    <Select
                      value={row.viewType ?? '__unmapped__'}
                      onValueChange={value =>
                        handleViewTypeChange(row, value === '__unmapped__' ? '' : value)
                      }
                      disabled={createMutation.isPending || updateMutation.isPending}
                    >
                      <SelectTrigger
                        className={`w-[200px] ml-auto ${
                          row.viewType === null ? 'text-muted-foreground italic' : ''
                        }`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__unmapped__" className="italic text-muted-foreground">
                          {viewTypeLabel(null)}
                        </SelectItem>
                        {ViewTypeEnum.options.map(v => (
                          <SelectItem key={v} value={v}>
                            {viewTypeLabel(v)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={2} className="text-sm text-muted-foreground">
                    {t('cards.statusMappings.loading')}
                  </TableCell>
                </TableRow>
              )}
              {mappingsQuery.error && (
                <TableRow>
                  <TableCell colSpan={2} className="text-sm text-foreground">
                    {t('cards.statusMappings.error')}
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && allRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={2} className="text-sm text-muted-foreground">
                    {t('cards.statusMappings.empty')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        </ScrollReveal>
      </div>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteTarget !== null} onOpenChange={open => !open && setDeleteTarget(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {t('cards.statusMappings.deleteDialog.title')}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {t('cards.statusMappings.deleteDialog.description', {
              name: deleteTarget?.name,
            })}
          </p>
          <DialogFooter className="flex flex-row justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteTarget(null)}
              disabled={deleteMutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending
                ? t('cards.statusMappings.buttons.removing')
                : t('cards.statusMappings.buttons.remove')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default StatusMappingsPage
